import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2, FileText, TrendingUp, Award, ChevronRight, Check,
  Briefcase, Users, BarChart3, MapPin, Mail, Sparkles
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Tier = ({ name, price, billing, features, badge, accent = 'cyan', onCTA, popular }) => {
  const accentMap = {
    cyan:   { bar: 'bg-cyan-500',   ring: 'ring-cyan-500',   text: 'text-cyan-700',   btn: 'bg-cyan-600 hover:bg-cyan-700' },
    amber:  { bar: 'bg-amber-500',  ring: 'ring-amber-500',  text: 'text-amber-700',  btn: 'bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 hover:opacity-90' },
    slate:  { bar: 'bg-slate-700',  ring: 'ring-slate-700',  text: 'text-slate-700',  btn: 'bg-slate-800 hover:bg-slate-900' },
  };
  const a = accentMap[accent];
  return (
    <div
      className={`relative bg-white rounded-2xl shadow-sm border border-slate-200 p-7 flex flex-col ${
        popular ? `ring-2 ${a.ring} ring-offset-2` : ''
      }`}
      data-testid={`pricing-tier-${name.toLowerCase()}`}
    >
      <div className={`absolute top-0 left-0 right-0 h-1.5 ${a.bar} rounded-t-2xl`} />
      {popular && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-400 to-rose-500 text-white text-xs font-black uppercase tracking-wider px-3 py-1 rounded-full shadow">
          Most Popular
        </span>
      )}
      {badge && (
        <span className={`inline-block self-start text-xs font-bold uppercase tracking-wider ${a.text} mb-2`}>
          {badge}
        </span>
      )}
      <h3 className="text-2xl font-black text-slate-900 mb-1">{name}</h3>
      <div className="flex items-baseline gap-1 mb-1">
        <span className="text-4xl font-black text-slate-900">{price}</span>
        <span className="text-slate-500">/{billing}</span>
      </div>
      <ul className="space-y-2 mt-5 mb-7 flex-1">
        {features.map((f, i) => (
          <li key={i} className="flex gap-2 text-sm text-slate-700">
            <Check className={`w-5 h-5 ${a.text} shrink-0 mt-0.5`} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={onCTA}
        data-testid={`pricing-cta-${name.toLowerCase()}`}
        className={`${a.btn} text-white font-bold py-3 rounded-full shadow-md active:scale-95 transition-all flex items-center justify-center gap-2`}
      >
        Request {name}
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};

const ForPartnersPage = () => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    org_name: '',
    venue_type: 'Museum',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    interest_tier: 'Insights',
    message: '',
  });

  const openForm = (tier) => {
    setForm((f) => ({ ...f, interest_tier: tier }));
    setShowForm(true);
    setTimeout(() => {
      document.querySelector('[data-testid="partner-lead-form"]')?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.org_name.trim() || !form.contact_email.trim() || !form.contact_name.trim()) {
      toast.error('Please fill the required fields.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/partner-leads`, form);
      toast.success("Thanks! Hadas will reach out within 1 business day.");
      setShowForm(false);
      setForm((f) => ({ ...f, message: '' }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit, please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50 pb-24">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-r from-slate-900 via-cyan-900 to-slate-900 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(6,182,212,0.25),transparent_60%)]" />
        <div className="relative max-w-6xl mx-auto px-4 py-20 sm:py-28">
          <button
            onClick={() => navigate('/')}
            className="text-cyan-200 hover:text-white text-sm font-semibold mb-6"
            data-testid="back-home-btn"
          >
            ← Back to Audiostride
          </button>
          <span className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-6">
            <Sparkles className="w-3.5 h-3.5 text-amber-300" />
            For museums · cities · attractions · tourism boards
          </span>
          <h1 className="text-4xl sm:text-6xl font-black tracking-tight leading-tight mb-5 max-w-4xl">
            Visitor-behavior intelligence,<br />
            <span className="bg-gradient-to-r from-amber-300 via-rose-300 to-cyan-300 bg-clip-text text-transparent">
              consulting-grade.
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-cyan-100/90 max-w-3xl mb-10">
            Premium monthly analytics on how visitors actually move through your venue —
            forensic diagnosis, USD attention value, and a prioritized action plan in your
            own currency: retail, sponsorship, programming, membership.
            <span className="text-white font-semibold"> Independent of any tour revenue.</span>
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => openForm('Insights')}
              data-testid="hero-cta-pilot"
              className="bg-gradient-to-r from-amber-400 to-rose-500 hover:opacity-90 text-white font-black px-7 py-4 rounded-full shadow-xl active:scale-95 transition-all"
            >
              Request your free first report
            </button>
            <button
              onClick={() => document.getElementById('what-you-get')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="hero-cta-preview"
              className="bg-white/10 hover:bg-white/20 backdrop-blur border border-white/30 text-white font-bold px-7 py-4 rounded-full transition-all"
            >
              See a sample report
            </button>
          </div>
          <p className="text-cyan-200/70 text-xs mt-6">
            Pilot partners: first month free · No tech setup · Aggregated, anonymized data only
          </p>
        </div>
      </section>

      {/* What you get */}
      <section id="what-you-get" className="max-w-6xl mx-auto px-4 py-20">
        <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mb-3">
          A 13-page report your board can read in 10 minutes.
        </h2>
        <p className="text-lg text-slate-600 max-w-3xl mb-12">
          Built like the work of a senior visitor-behavior consultant — except it lands in your inbox every month, every quarter, or every week. Every recommendation is a thing your team can do, not edits to a tour you don't control.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { i: TrendingUp, t: 'Letter-grade Executive Scorecard', d: 'Tour Health, Visitor Satisfaction, Visitor Intelligence, Growth Momentum — A through F with trend arrows. Boards understand it instantly.' },
            { i: Award,      t: 'Visitor Attention Value (USD)', d: 'Branded engagement time + impression equivalent + loyalty value + completion premium. Use it to size sponsor packages or grant impact statements.' },
            { i: BarChart3,  t: 'Forensic Diagnosis', d: 'Specific visitor-behavior findings tied to real stop names, with evidence and a confidence rating on each. No generic fluff.' },
            { i: Briefcase,  t: 'Prioritized Action Plan', d: 'Each recommendation owns a department (Retail, Sponsorships, Visitor Services, Programming) and quantifies projected lift in YOUR currency.' },
            { i: MapPin,     t: 'Stop-by-Stop Analytics', d: 'Per-area dwell time, completion, replay, drop-off contribution. Reveals where in your space visitors linger or leave.' },
            { i: FileText,   t: '30/60/90-day Forecast', d: 'Linear-projection trajectory with confidence rating + anomaly flags so you know what to plan for next quarter.' },
            { i: Users,      t: 'Audience Insights', d: 'Total/returning/unique visitors, peak hour windows, day-of-week distribution, device split.' },
            { i: Building2,  t: 'Audiostride or White-Label', d: 'Co-branded with your logo by default; full white-label available on Enterprise.' },
            { i: Mail,       t: 'Auto-emailed PDF', d: 'Lands in the recipients you designate, on the cadence you choose. Plus a self-service portal to view history.' },
          ].map(({ i: Icon, t, d }, idx) => (
            <div key={idx} className="bg-white rounded-2xl p-6 border border-slate-200 hover:border-cyan-300 hover:shadow-md transition-all">
              <Icon className="w-7 h-7 text-cyan-600 mb-3" />
              <h3 className="font-bold text-slate-900 mb-1.5">{t}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-gradient-to-r from-cyan-50 to-amber-50 border-2 border-cyan-200 rounded-2xl p-8 text-center">
          <h3 className="text-xl font-black text-slate-900 mb-2">
            Want to see what a real report looks like?
          </h3>
          <p className="text-slate-600 mb-5">
            We'll send you a free sample tailored to your venue — no signup, no card.
          </p>
          <button
            onClick={() => openForm('Insights')}
            data-testid="midpage-cta"
            className="bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 hover:opacity-90 text-white font-black px-7 py-3 rounded-full shadow-lg active:scale-95 transition-all inline-flex items-center gap-2"
          >
            Request your free first report <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-6xl mx-auto px-4 py-20">
        <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mb-3">
          Pricing that scales with your venue.
        </h2>
        <p className="text-lg text-slate-600 max-w-3xl mb-12">
          Per tour. All tiers include a comprehensive <strong>annual strategic briefing</strong> after 12 months. Annual billing saves two months. USD only at the moment.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Tier
            name="Insights"
            price="$199"
            billing="month"
            badge="Single attractions · small museums"
            accent="cyan"
            onCTA={() => openForm('Insights')}
            features={[
              'Monthly 13-page premium PDF report',
              'Up to 3 recipients',
              'Audiostride or co-branded with your logo',
              'Self-service web portal access',
              'Annual strategic briefing after 12 months',
              '$1,990/yr (2 months free)',
            ]}
          />
          <Tier
            name="Performance"
            price="$399"
            billing="month"
            badge="Mid-sized museums · tourism boards"
            accent="amber"
            popular
            onCTA={() => openForm('Performance')}
            features={[
              'Weekly + monthly + quarterly summary reports',
              'Up to 10 recipients',
              'Co-branded with your logo',
              'Self-service web portal access',
              'Annual strategic briefing after 12 months',
              '$3,990/yr (2 months free)',
            ]}
          />
          <Tier
            name="Enterprise"
            price="from $999"
            billing="month · custom"
            badge="Cities · multi-property attractions"
            accent="slate"
            onCTA={() => openForm('Enterprise')}
            features={[
              'Daily / weekly / custom-cadence reports',
              'Unlimited recipients',
              'Full white-label (your brand only)',
              'Multi-tour included',
              'Dedicated account manager',
              'Annual strategic briefing after 12 months',
            ]}
          />
        </div>

        <p className="text-center text-sm text-slate-500 mt-8">
          🎯 <strong>Pilot offer for early partners:</strong> First month free + white-glove onboarding. Limited slots.
        </p>
      </section>

      {/* Lead form */}
      {showForm && (
        <section className="max-w-2xl mx-auto px-4 py-12">
          <form
            onSubmit={submit}
            className="bg-white rounded-2xl border-2 border-cyan-200 shadow-xl p-6 sm:p-8"
            data-testid="partner-lead-form"
          >
            <h3 className="text-2xl font-black text-slate-900 mb-1">Request your free first report</h3>
            <p className="text-slate-600 mb-6">Hadas (founder) will personally follow up within one business day.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-1">Organization name *</label>
                <input
                  type="text"
                  value={form.org_name}
                  onChange={(e) => setForm({ ...form, org_name: e.target.value })}
                  required
                  data-testid="lead-org-input"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Venue type</label>
                <select
                  value={form.venue_type}
                  onChange={(e) => setForm({ ...form, venue_type: e.target.value })}
                  data-testid="lead-venue-type"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 bg-white"
                >
                  {['Museum', 'City / tourism board', 'Attraction', 'Heritage site', 'Other'].map((v) => <option key={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Tier of interest</label>
                <select
                  value={form.interest_tier}
                  onChange={(e) => setForm({ ...form, interest_tier: e.target.value })}
                  data-testid="lead-tier"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 bg-white"
                >
                  {['Insights', 'Performance', 'Enterprise', 'Just curious'].map((v) => <option key={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Your name *</label>
                <input
                  type="text"
                  value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  required
                  data-testid="lead-name-input"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                  required
                  data-testid="lead-email-input"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-1">Phone (optional)</label>
                <input
                  type="tel"
                  value={form.contact_phone}
                  onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                  data-testid="lead-phone-input"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-1">What would you like the report to cover?</label>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={3}
                  data-testid="lead-message-input"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 resize-none"
                  placeholder="e.g. visitor flow through our main floor, where to place a new sponsor activation, drop-off causes…"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="lead-submit-btn"
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:opacity-90 disabled:opacity-50 text-white font-black py-4 rounded-full shadow-lg active:scale-95 transition-all"
            >
              {submitting ? 'Sending…' : 'Send my request'}
            </button>
            <p className="text-xs text-slate-500 mt-3 text-center">
              We&apos;ll never share your details. Reply unsubscribe anytime.
            </p>
          </form>
        </section>
      )}
    </div>
  );
};

export default ForPartnersPage;
